import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { getTenant } from "../Tenants.slice";
import { pushToast } from "../../../../components/Toaster/Toaster.slice";
import { getPaymentListApi, updatePaymentApi } from "../../../../apis/apis";
import Table from "../../../../components/Table/Table";
import Badge from "../../../../components/Badge";
import Button from "../../../../components/Form/Button";
import ActionMenu from "../../../../components/ActionMenu";
import InlineEditInput from "../../../../components/Form/InlineEditInput";
import PostTenantPaymentModal from "./PostTenantPaymentModal";
import PostRefundsModal from "./PostRefundsModal";
import MakeACHPaymentModal from "./MakeACHPaymentModal";
import MakeCreditCardPaymentModal from "./MakeCreditCardPaymentModal";
import { Tooltip } from "flowbite-react";
import { formatUTCDate, formatCurrency } from "../../../../util/string";
import { Payment, ServiceAccount } from "../../../../apis/types";
import { useAppDispatch, useAppSelector } from "../../../../shared/redux/hooks";
import { getInputStyles } from "../../../../components/Form/styles";
import { useTabHeaderActions } from "./TabPanel";
import { getPaymentBalanceReferences } from "./paymentAmountBalanceWarningUtil";

const getPaymentTypeMap = (key: string) => {
  type paymentType = "ONE_TIME_PAYMENT" | "AUTOPAY";
  const paymentTypeMap = {
    ONE_TIME_PAYMENT: "One-time payment",
    AUTOPAY: "AutoPay",
  };
  return paymentTypeMap[(key as unknown as paymentType) || "Unknown"];
};

export interface TabPaymentsProps {
  title: string;
  badge?: number | string;
  serviceAccount: ServiceAccount;
}

const PAYMENTS_PREVIEW_LIMIT = 5

const TabPayments: React.FC<TabPaymentsProps> = (props) => {
  const { serviceAccount, title: _title, badge: _badge, ...rest } = props;
  const dispatch = useAppDispatch();
  const { permissions } = useAppSelector((state) => state.app);
  const { refreshingTenant } = useAppSelector((state) => state.tenants);

  const { tenantId } = useParams();
  const [currentTenantId, setCurrentTenantId] = useState(tenantId);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [isPostPaymentModalOpen, setIsPostPaymentModalOpen] = useState(false);
  const [isPostRefundsModalOpen, setIsPostRefundsModalOpen] = useState(false);
  const [isACHModalOpen, setIsACHModalOpen] = useState(false);
  const [isCreditCardModalOpen, setIsCreditCardModalOpen] = useState(false);
  
  // Status change date picker state
  const [activeStatusChangePaymentId, setActiveStatusChangePaymentId] = useState<string | null>(null);
  const [activeStatusChangeNewStatus, setActiveStatusChangeNewStatus] = useState<string | null>(null);
  const [showStatusChangeDatePicker, setShowStatusChangeDatePicker] = useState(false);
  const [customStatusChangeDate, setCustomStatusChangeDate] = useState('');
  const [useOriginalStatusChangeDate, setUseOriginalStatusChangeDate] = useState(true);
  const [dropdownCoords, setDropdownCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isStatusChangeProcessing, setIsStatusChangeProcessing] = useState(false);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const wasRefreshingTenant = useRef(false);

  const hasOneTimePaymentProcessorPermission =
    permissions != null && permissions.includes('onetimepayprocessor:take:payment')
  const canOneTimePay =
    hasOneTimePaymentProcessorPermission &&
    serviceAccount.currentInvoice != null &&
    serviceAccount.accountManagers.length > 0

  const { balanceTotal, balanceDue } = getPaymentBalanceReferences(serviceAccount)
  const oneTimeUserOptions = serviceAccount.accountManagers.reduce(
    (a, v) => ({
      ...a,
      [v.id]: `${v.profile?.firstName !== undefined ? `${v.profile?.firstName} ` : ''} ${
        v.profile?.lastName !== undefined ? `${v.profile?.lastName} — ` : ''
      } ${v.email}`,
    }),
    {} as Record<string, string>
  )

  const openPostPaymentRef = useRef(() => setIsPostPaymentModalOpen(true));
  openPostPaymentRef.current = () => setIsPostPaymentModalOpen(true);
  const openPostRefundsRef = useRef(() => setIsPostRefundsModalOpen(true));
  openPostRefundsRef.current = () => setIsPostRefundsModalOpen(true);
  const openAchRef = useRef(() => setIsACHModalOpen(true));
  openAchRef.current = () => setIsACHModalOpen(true);
  const openCreditCardRef = useRef(() => setIsCreditCardModalOpen(true));
  openCreditCardRef.current = () => setIsCreditCardModalOpen(true);

  const paymentHeaderActions = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => openPostPaymentRef.current()} size={2}>
          Tenant payment
        </Button>
        {canOneTimePay && (
          <ActionMenu
            variant="button"
            label="One-time payment"
            options={[
              { label: 'ACH', value: 'ach' },
              { label: 'Credit card', value: 'cc' },
            ]}
            handleChange={async (value) => {
              if (value === 'ach') openAchRef.current()
              if (value === 'cc') openCreditCardRef.current()
            }}
          />
        )}
        <Button onClick={() => openPostRefundsRef.current()} size={2} color="slate">
          Refund
        </Button>
      </div>
    ),
    [canOneTimePay]
  );
  useTabHeaderActions(paymentHeaderActions, serviceAccount.currentInvoice != null);

  const loadPayments = useCallback(async () => {
    setLoadingPayments(true)
    try {
      const response = await getPaymentListApi({
        tenantAccountId: serviceAccount.id,
        resultsPerPage: PAYMENTS_PREVIEW_LIMIT,
        page: 0,
      })
      setPayments(response.results ?? [])
    } catch (error) {
      console.error('Failed to load payments for account', serviceAccount.id, error)
      setPayments([])
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to load payments',
        })
      )
    } finally {
      setLoadingPayments(false)
    }
  }, [serviceAccount.id, dispatch])

  useEffect(() => {
    setCurrentTenantId(tenantId);
  }, [tenantId]);

  useEffect(() => {
    setIsPostPaymentModalOpen(false);
    setIsPostRefundsModalOpen(false);
    setIsACHModalOpen(false);
    setIsCreditCardModalOpen(false);
  }, [serviceAccount.id]);

  useEffect(() => {
    void loadPayments()
  }, [loadPayments])

  // Refresh list after tenant-wide mutations (post payment, status change, etc.)
  useEffect(() => {
    if (refreshingTenant) {
      wasRefreshingTenant.current = true
      return
    }
    if (!wasRefreshingTenant.current) return
    wasRefreshingTenant.current = false
    void loadPayments()
  }, [refreshingTenant, loadPayments])

  const formatPaymentMethodDisplay = (payment: Payment): string => {
    const providerName = payment.paymentMethod?.providerName ?? payment.providerName
    const last4 = payment.paymentMethod?.last4 ?? payment.last4
    if (providerName === 'CHECK') return 'Check'
    if (providerName === 'DIRECT_DEPOSIT') return 'Direct deposit'
    if (providerName == null || providerName === '') return '—'
    if (last4 == null || last4 === '') return providerName
    return `${providerName} (••••${last4})`
  }

  const transformResponse = (
    response: Payment[]
  ): Array<Payment & { display: string; paymentTypeDisplay: string }> =>
    response.map((res) => {
      const { type } = res;
      const [paymentType] = type?.split(" - ") ?? [];
      return {
        ...res,
        display: formatPaymentMethodDisplay(res),
        paymentTypeDisplay: getPaymentTypeMap(paymentType),
      }
    });

  const handlePaymentAction = async (
    payment: Payment,
    newStatus: string
  ): Promise<void> => {
    // Show date picker popup for all status changes
    handleStatusChangeClick(payment.id, newStatus);
  };
  
  // Handle status change date picker popup
  const handleStatusChangeClick = (paymentId: string, newStatus: string) => {
    setActiveStatusChangePaymentId(paymentId);
    setActiveStatusChangeNewStatus(newStatus);
    setShowStatusChangeDatePicker(true);
    setUseOriginalStatusChangeDate(true);
    setCustomStatusChangeDate('');
    
    // Position popup in center of screen (simplified positioning)
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = 320;
    const popupHeight = 280;
    
    setDropdownCoords({
      x: (viewportWidth - popupWidth) / 2,
      y: (viewportHeight - popupHeight) / 2,
    });
  };
  
  // Handle status change confirmation with date
  const handleStatusChangeConfirm = async () => {
    if (!activeStatusChangePaymentId || !activeStatusChangeNewStatus) return;

    const payment = payments.find(p => p.id === activeStatusChangePaymentId);
    if (!payment) return;

    setIsStatusChangeProcessing(true);

    try {
      const originalDate = new Date(payment.paymentDate).toISOString().split('T')[0];

      const apiPayload: any = {
        paymentId: payment.id,
        newStatus: activeStatusChangeNewStatus
      };

      // Only include transactionDate if user selected custom date option
      if (!useOriginalStatusChangeDate) {
        apiPayload.transactionDate = customStatusChangeDate || originalDate;
      }
      await updatePaymentApi(apiPayload);

      dispatch(
        pushToast({
          type: "success",
          message: `Payment ${activeStatusChangeNewStatus.toLowerCase()}`,
          description: apiPayload.transactionDate ? `Transaction date set to ${formatUTCDate(new Date(apiPayload.transactionDate))}` : undefined,
        })
      );

      if (currentTenantId !== null) {
        dispatch(getTenant(tenantId ?? ""));
      }
    } catch (e: any) {
      dispatch(
        pushToast({
          type: "error",
          message: `Error updating payment status to ${activeStatusChangeNewStatus}`,
          description: `${
            e.response?.data?.message !== undefined
              ? `${e.response?.data?.message as string}:`
              : "Error:"
          } ${e.message as string}`,
        })
      );
    } finally {
      setIsStatusChangeProcessing(false);
      setActiveStatusChangePaymentId(null);
      setActiveStatusChangeNewStatus(null);
      setShowStatusChangeDatePicker(false);
      setUseOriginalStatusChangeDate(true);
      setCustomStatusChangeDate('');
    }
  };
  
  // Handle status change cancel
  const handleStatusChangeCancel = () => {
    setActiveStatusChangePaymentId(null);
    setActiveStatusChangeNewStatus(null);
    setShowStatusChangeDatePicker(false);
    setUseOriginalStatusChangeDate(true);
    setCustomStatusChangeDate('');
  };

  const hasPaymentOperationsPermissions = (): boolean => {
    if (permissions == null) return false;
    return (
      permissions.includes("payops:cancel:payment") &&
      permissions.includes("payops:update:payment")
    );
  };

  if (serviceAccount.currentInvoice === undefined) {
    return (
      <div>An invoice has not yet been posted for this service account</div>
    );
  }

  if (loadingPayments && payments.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <>
      {payments.length > 0 ? (
        <>
          <Table<Payment>
            compact
            stickyHeader
            scrollShell={{ heightClassName: 'max-h-64 sm:max-h-80 md:max-h-96 lg:max-h-[45vh]' }}
            colConfig={[
              {
                label: 'Payment Number',
                render: (record) => record.paymentNumber,
              },
              {
                label: 'Amount',
                render: (record) => formatCurrency(record.amount),
              },
              {
                label: 'Frequency',
                render: (record) => record.paymentTypeDisplay,
              },
              {
                label: 'Type',
                render: (record) => record.display,
              },
              {
                label: 'Payment Date',
                render: (record) => formatUTCDate(new Date(record.paymentDate)),
              },
              {
                label: 'Posting Date',
                render: (record) => formatUTCDate(new Date(record.postingDate)),
              },
              {
                label: 'Memo',
                columnMinWidth: 200,
                cellClassName: 'overflow-visible',
                render: (record) => (
                  <InlineEditInput
                    name="memo"
                    label="Payment memo"
                    hideLabel
                    className="min-w-[180px]"
                    initialValue={record.memo}
                    handleSubmit={async (editedValue) => {
                      await updatePaymentApi({
                        paymentId: record.id,
                        memo: editedValue ?? undefined,
                      })
                      dispatch(getTenant(currentTenantId ?? ''))
                    }}
                  />
                ),
              },
              {
                label: 'Status',
                render: (record) => {
                  if (record.status === 'FAILED' && record.failureReason) {
                    return (
                      <Tooltip
                        content={record.failureReason}
                        placement="top"
                        className="text-xs z-[100] pointer-events-none select-none bg-slate-600 dark:bg-slate-950 border dark:border-0"
                      >
                        <Badge status={record.status} />
                      </Tooltip>
                    );
                  }
                  return <Badge status={record.status} />;
                },
              },
              ...(hasPaymentOperationsPermissions()
                ? [
                    {
                      label: 'Change status',
                      isHidden: payments.filter((payment) => payment.nextStatus?.length ?? 0 > 0).length === 0,
                      render: (record: Payment) => (
                        <div>
                          <ActionMenu
                            handleChange={async (status) => await handlePaymentAction(record, status ?? '')}
                            options={record.nextStatus?.map((nextStatus) => ({
                              value: nextStatus,
                            }))}
                          />
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
            hidePaginator
            data={{
              results: transformResponse(payments),
            }}
            {...rest}
          />
          {payments.length >= PAYMENTS_PREVIEW_LIMIT && (
            <div className="mt-6">
              <Link
                to={`/collections/payments?tenantId=${currentTenantId ?? ''}&tenantAccountId=${serviceAccount.id}`}
                className="inline-block rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                View all tenant account payments
              </Link>
            </div>
          )}
        </>
      ) : (
        <span>No payments exist yet for this service account</span>
      )}

      {serviceAccount.currentInvoice != null && (
        <>
          <PostTenantPaymentModal
            isOpen={isPostPaymentModalOpen}
            onClose={() => setIsPostPaymentModalOpen(false)}
            serviceAccount={serviceAccount}
          />
          <PostRefundsModal
            isOpen={isPostRefundsModalOpen}
            onClose={() => setIsPostRefundsModalOpen(false)}
            serviceAccount={serviceAccount}
          />
          {canOneTimePay && (
            <>
              <MakeACHPaymentModal
                isOpen={isACHModalOpen}
                onClose={() => setIsACHModalOpen(false)}
                userOptions={oneTimeUserOptions}
                defaultUserId={serviceAccount.accountManagers[0].id}
                invoiceId={serviceAccount.currentInvoice.id}
                balanceTotal={balanceTotal}
                balanceDue={balanceDue}
              />
              <MakeCreditCardPaymentModal
                isOpen={isCreditCardModalOpen}
                onClose={() => setIsCreditCardModalOpen(false)}
                userOptions={oneTimeUserOptions}
                defaultUserId={serviceAccount.accountManagers[0].id}
                invoiceId={serviceAccount.currentInvoice.id}
                balanceTotal={balanceTotal}
                balanceDue={balanceDue}
              />
            </>
          )}
        </>
      )}

      {/* Status change date picker popup */}
      {activeStatusChangePaymentId && showStatusChangeDatePicker && (() => {
        const payment = payments.find(p => p.id === activeStatusChangePaymentId);
        if (!payment) return null;

        const originalDate = new Date(payment.paymentDate).toISOString().split('T')[0];

        return (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={handleStatusChangeCancel}
            />

            {/* Popup */}
            <div
              className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[320px]"
              style={{
                left: dropdownCoords.x,
                top: dropdownCoords.y,
              }}
              ref={(el) => {
                if (activeStatusChangePaymentId) {
                  dropdownRefs.current[activeStatusChangePaymentId] = el;
                }
              }}
            >
              <div className="mb-4">
                <div className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {activeStatusChangeNewStatus === 'CHARGEBACK' ? 'Chargeback Payment' : `Change Status to ${activeStatusChangeNewStatus}`}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Payment #{payment.paymentNumber}<br/>
                  Amount: {formatCurrency(payment.amount)}<br/>
                  Original Date: {formatUTCDate(new Date(payment.paymentDate))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Transaction Date
                </label>
                <div className="space-y-3">
                  <label className="flex items-center text-sm">
                    <input
                      type="radio"
                      name={`statusChangeDateOption-${payment.id}`}
                      checked={useOriginalStatusChangeDate}
                      onChange={() => setUseOriginalStatusChangeDate(true)}
                      className="mr-3 text-indigo-600"
                    />
                    Use payment date ({formatUTCDate(new Date(payment.paymentDate))})
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="radio"
                      name={`statusChangeDateOption-${payment.id}`}
                      checked={!useOriginalStatusChangeDate}
                      onChange={() => setUseOriginalStatusChangeDate(false)}
                      className="mr-3 text-indigo-600"
                    />
                    Custom transaction date
                  </label>
                </div>
              </div>

              {!useOriginalStatusChangeDate && (
                <div className="mb-4">
                  <input
                    type="date"
                    value={customStatusChangeDate || originalDate}
                    onChange={(e) => setCustomStatusChangeDate(e.target.value)}
                    className={getInputStyles()}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <div className="flex-1">
                  <Button
                    size={2}
                    disabled={isStatusChangeProcessing}
                    onClick={handleStatusChangeConfirm}
                    className="w-full"
                  >
                    {isStatusChangeProcessing ? (
                      <>
                        <svg
                          className="mr-2 h-4 w-4 inline-block animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Confirm
                      </>
                    ) : (
                      `Confirm ${activeStatusChangeNewStatus}`
                    )}
                  </Button>
                </div>
                <div className="flex-1">
                  <Button
                    size={2}
                    color="slate"
                    disabled={isStatusChangeProcessing}
                    onClick={handleStatusChangeCancel}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </>
  )
};

export default TabPayments;
